import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ConfirmationService} from 'primeng/api';

import {UserService} from '@app/core';
import {AppraisalAccessedBy, EmployeeAppraisalHistory, Format, PrimeNgSupportService, TableColumn, Validation} from '@app/shared';
import {AppraisalHistoryDataService} from './appraisal-history-data.service';
import {classToClass, plainToClass} from 'class-transformer';

@Component({
  selector: 'app-appraisal-history',
  templateUrl: './appraisal-history.component.html',
  styleUrls: ['./appraisal-history.component.css'],
  providers: [AppraisalHistoryDataService],
})

/**
 * @author Archana Anand (date: May 2018)
 * @desc Appraisal History component that gets details of an employee appraisal history to be displayed in a table with CRUD operations
 * @desc 09/20 Jin C - disabled CRUD operations. It is read only table.
 * */

export class AppraisalHistoryComponent implements OnInit, OnChanges {

  @Input() accessedBy: AppraisalAccessedBy;           // type of user trying to access the appraisal history
  @Input() userId: number;                            // user id of the retrieve appraisal history

  // standard format and validation
  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;

  tableColumns: TableColumn[];                                // column headers and fields to display on the table

  appraisalHistories: EmployeeAppraisalHistory[];             // user's appraisal history to show
  editable: boolean = false;

  // dialog supporting attributes
  maxEndOn: Date;                                     // Maximum date for validations
  displayDialog: boolean;                             // whether the dialog for add/edit should be displayed or not
  appraisalHistoryToEdit: EmployeeAppraisalHistory;           // appraisal history to be added/edited

  constructor(private confirmationService: ConfirmationService, private user: UserService, private dataService: AppraisalHistoryDataService,  private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.tableColumns = [
      {field: 'appraisalYear', header: 'Year'},
      {field: 'appraisalFrom', header: 'Appraisal From'},
      {field: 'appraisalThru', header: 'Appraisal Thru'},
      {field: 'employeeRatingName', header: 'Self Rating'},
      {field: 'managerRatingName', header: 'Manager Rating'},
      {field: 'employeeSummary', header: 'Appraisal Summary'},
      {field: 'managerSummary', header: 'Manager Summary'},
    ];
    if (this.editable) {
      this.tableColumns.push({field: 'action', header: 'Action'});
      this.maxEndOn = new Date();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (this.accessedBy == AppraisalAccessedBy.Self) {
      this.dataService.getMyAppraisalHistory().subscribe(res => {
        this.appraisalHistories = plainToClass(EmployeeAppraisalHistory, res.list);
      });
    } else if (this.userId) {
      // FixMe: need to check the security here

      this.dataService.searchAppraisalHistory(this.userId).subscribe(res => {
        this.appraisalHistories = plainToClass(EmployeeAppraisalHistory, res.list);
      });
    } else {
      console.warn("userId is null");
    }
  }

  /**
   * @desc show a pop-up dialog to gather new Appraisal History information
   */
  onClickAddRow(): void {

    this.appraisalHistoryToEdit = new EmployeeAppraisalHistory();
    this.appraisalHistoryToEdit.userId = this.user.info.userId;
    this.displayDialog = true;
  }

  /**
   * @desc edit an existing Appraisal History
   * @param employeeAppraisalHistory - selected Appraisal History to edit
   */
  onClickEditRow(employeeAppraisalHistory: EmployeeAppraisalHistory): void {

    this.appraisalHistoryToEdit = classToClass(employeeAppraisalHistory);
    // this.appraisalHistoryToEdit.userId = this.user.info.userId;
    this.displayDialog = true;
  }

  /**
   * @desc save new Appraisal History to the inputArray
   * @param appraisalHistory - either new or edited Appraisal History for save
   */
  onClickSaveRow(appraisalHistory: EmployeeAppraisalHistory): void {

    if (this.appraisalHistories && appraisalHistory) {
      if (appraisalHistory.id) {
        const index: number = this.appraisalHistories.findIndex(aH => (aH.id == appraisalHistory.id));
        this.appraisalHistories = this.primeNgService.replaceByIndex(this.appraisalHistories, appraisalHistory, index);
      } else {
        this.appraisalHistories = this.primeNgService.unshift(this.appraisalHistories, appraisalHistory);
      }
    }
    this.displayDialog = false;
  }

  /**
   * @desc delete the selected row from the inputArray
   * @param employeeAppraisalHistory
   */
  onClickDeleteRow(employeeAppraisalHistory: EmployeeAppraisalHistory): void {

    const rowIndex = this.appraisalHistories.indexOf(employeeAppraisalHistory);
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this Appraisal History?',
      header: 'Delete Appraisal History',
      icon: 'fa fa-question-circle',
      accept: () => {
        if (rowIndex >= 0) {
          this.appraisalHistories =  this.primeNgService.deleteByIndex(this.appraisalHistories, rowIndex);
        }
      },
      reject: () => {
      }
    });
  }

  /**
   * @desc saves/updates all the appraisal history in the table and the response is updated to the inputArray;
   */
  onClickSaveAppraisalHistory(): void {

    this.dataService.updateAppraisalHistory(this.appraisalHistories).subscribe(apprHistory => {
      this.appraisalHistories = plainToClass(EmployeeAppraisalHistory, apprHistory.list as EmployeeAppraisalHistory[]);
    });
  }

}
